import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FileSpreadsheet,
  Download,
  Printer,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Users,
  AlertCircle,
  FileDown,
  Search,
  IndianRupee,
  Calendar,
  Building2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PendingSalesByClientReportPage = () => {
  usePageTitle("Pending Sales by Client Report");
  const { token } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const fetchReport = async () => {
    setLoading(true);
    try {
      const url = `${BACKEND_URL}/api/reports/pending-sales-by-client?start_year=2018`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Failed to fetch report");
      const data = await response.json();
      setReportData(data);
    } catch (error) {
      toast.error("Failed to load report: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [token]);

  const toggleClient = (clientCode) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientCode)) {
      newExpanded.delete(clientCode);
    } else {
      newExpanded.add(clientCode);
    }
    setExpandedClients(newExpanded);
  };

  const filteredClients = reportData?.clients?.filter(client =>
    client.client_code.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const exportToPDF = () => {
    if (!reportData) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text("Pending Sales by Client Report", pageWidth / 2, 20, { align: "center" });
    
    // Subtitle
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: "center" });
    
    // Summary
    doc.setFontSize(12);
    doc.setTextColor(40);
    doc.text("Summary", 14, 40);
    
    doc.autoTable({
      startY: 45,
      head: [["Metric", "Value"]],
      body: [
        ["Total Clients with Pending Sales", reportData.summary.total_clients_pending],
        ["Total Pending Months", reportData.summary.total_pending_months],
        ["Total Pending Value", formatCurrency(reportData.summary.total_pending_value)]
      ],
      theme: "grid",
      headStyles: { fillColor: [220, 38, 38] }
    });
    
    // Client-wise breakdown
    let yPos = doc.lastAutoTable.finalY + 15;
    
    filteredClients.forEach((client, idx) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(11);
      doc.setTextColor(40);
      doc.text(`${idx + 1}. ${client.client_code}`, 14, yPos);
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Pending: ${client.pending_months} months | Value: ${formatCurrency(client.pending_value)}`, 14, yPos + 5);
      
      const monthRows = [];
      client.years.forEach(year => {
        year.months.forEach(m => {
          monthRows.push([
            year.year,
            m.month_name,
            m.order_count.toLocaleString(),
            formatCurrency(m.order_value)
          ]);
        });
      });
      
      doc.autoTable({
        startY: yPos + 10,
        head: [["Year", "Month", "Orders", "Value"]],
        body: monthRows,
        theme: "striped",
        headStyles: { fillColor: [100, 116, 139] },
        styles: { fontSize: 8 }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    });
    
    doc.save("pending-sales-by-client-report.pdf");
    toast.success("PDF exported successfully");
  };

  const exportToExcel = () => {
    if (!reportData) return;
    
    // Create CSV content
    let csv = "Pending Sales by Client Report\n";
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    
    csv += "Summary\n";
    csv += `Total Clients Pending,${reportData.summary.total_clients_pending}\n`;
    csv += `Total Pending Months,${reportData.summary.total_pending_months}\n`;
    csv += `Total Pending Value,${reportData.summary.total_pending_value}\n\n`;
    
    csv += "Client Code,Year,Month,Orders,Value\n";
    filteredClients.forEach(client => {
      client.years.forEach(year => {
        year.months.forEach(m => {
          csv += `${client.client_code},${year.year},${m.month_name},${m.order_count},${m.order_value}\n`;
        });
      });
    });
    
    // Download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pending-sales-by-client-report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Excel/CSV exported successfully");
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-12 w-96" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 print:p-2">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-7 h-7 text-red-600" />
            Pending Sales by Client Report
          </h1>
          <p className="text-slate-500 mt-1">Auto-generated report showing clients with shipment data but missing sales uploads</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchReport}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={exportToPDF}>
                <FileDown className="w-4 h-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToExcel}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export as Excel/CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-xl font-bold">Pending Sales by Client Report</h1>
        <p className="text-sm text-gray-500">Generated: {new Date().toLocaleString()}</p>
      </div>

      {/* Summary Cards */}
      {reportData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Clients with Pending</p>
                  <p className="text-2xl font-bold text-red-700">{reportData.summary.total_clients_pending}</p>
                </div>
                <Building2 className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Pending Months</p>
                  <p className="text-2xl font-bold text-orange-700">{reportData.summary.total_pending_months}</p>
                </div>
                <Calendar className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Pending Value</p>
                  <p className="text-2xl font-bold text-purple-700">{formatCurrency(reportData.summary.total_pending_value)}</p>
                </div>
                <IndianRupee className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative print:hidden">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by client code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 max-w-sm"
        />
      </div>

      {/* Client-wise Breakdown */}
      {reportData && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Clients with Pending Sales ({filteredClients.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {filteredClients.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {searchTerm ? "No clients match your search" : "No pending sales data found"}
              </div>
            ) : (
              filteredClients.map((client, idx) => (
                <Collapsible
                  key={client.client_code}
                  open={expandedClients.has(client.client_code)}
                  onOpenChange={() => toggleClient(client.client_code)}
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg cursor-pointer border border-slate-200">
                      <div className="flex items-center gap-3">
                        {expandedClients.has(client.client_code) ? (
                          <ChevronDown className="w-5 h-5 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-500" />
                        )}
                        <span className="text-sm text-slate-400 w-6">{idx + 1}.</span>
                        <span className="font-semibold text-slate-800">{client.client_code}</span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                          {client.pending_months} months pending
                        </Badge>
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                          {formatCurrency(client.pending_value)}
                        </Badge>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="p-3 bg-white border border-t-0 border-slate-200 rounded-b-lg">
                      {client.years.map(year => (
                        <div key={year.year} className="mb-4 last:mb-0">
                          <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {year.year}
                          </h4>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Month</TableHead>
                                  <TableHead className="text-right">Orders</TableHead>
                                  <TableHead className="text-right">Value</TableHead>
                                  <TableHead>Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {year.months.map(month => (
                                  <TableRow key={month.year_month}>
                                    <TableCell className="font-medium">{month.month_name}</TableCell>
                                    <TableCell className="text-right">{month.order_count.toLocaleString()}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(month.order_value)}</TableCell>
                                    <TableCell>
                                      <Badge className="bg-red-100 text-red-700 border-red-300">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        Pending
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PendingSalesByClientReportPage;
